import { setupPPV } from "../../utils/paymentHelper";
import { ContentType } from "../../constants/ContentType";
import { buildHeader } from "../../services/baseService";

// Temporary Publishers feature:
//   /publishers          -> lists the parent tenant's publishers (sub tenants)
//   /publisher-content   -> shows a publisher's VOD content (?sub_tenant_id=)
// The app fetches a publisher's content through the existing vod-category
// endpoint by passing ?sub_tenant_id=<sub_tenant_uuid>. Purchases go through the
// shared setupPPV flow (plan-vs-PPV choice + multi-PPV selector), with the
// gateway chosen per owner so the rendered form matches where the charge lands:
// the publisher/category unlock uses the parent's gateway, the publisher's own
// content uses the sub tenant's gateway (which may be a different provider, e.g.
// parent Stripe + publisher Authorize.Net).

const STYLE_ID = "publishers-temp-style";

// Page context for the content page (set in renderContent), used by the
// delegated buy handler.
let PUB_CTX = { baseUrl: "", apiKey: "", subTenantId: "" };
const VIDEO_INDEX = {};

const STYLES = `
  #app-publishers, #app-publisher-content { color: #f5f5f7; font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
  .pub-wrap { max-width: 1200px; margin: 0 auto; padding: 32px 20px 48px; }
  .pub-wrap h1 { margin: 0 0 4px; font-size: 26px; }
  .pub-sub { margin: 0 0 24px; color: #9aa0a6; }
  .pub-back { color: #9aa0a6; text-decoration: none; font-size: 14px; }
  .pub-back:hover { color: #f5f5f7; }
  .pub-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 18px; }
  .pub-card { display: block; text-decoration: none; color: inherit; background: #1a1d24; border: 1px solid #262a33; border-radius: 10px; overflow: hidden; transition: transform .12s ease, border-color .12s ease; }
  .pub-card:hover { transform: translateY(-2px); border-color: #3a78ff; }
  .pub-thumb { position: relative; width: 100%; aspect-ratio: 16/9; background: #11141a; }
  .pub-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pub-ph { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #4b5563; font-size: 13px; }
  .pub-badge { position: absolute; top: 8px; right: 8px; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 10px; background: #3a78ff; color: #fff; }
  .pub-lock { position: absolute; top: 6px; right: 6px; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 10px; background: rgba(0,0,0,.7); color: #ffd166; }
  .pub-meta { padding: 12px 14px; }
  .pub-title { font-weight: 600; font-size: 15px; }
  .pub-category { margin-bottom: 32px; }
  .pub-category h2 { font-size: 18px; margin: 18px 0 12px; }
  .pub-row { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
  .pub-video { background: #1a1d24; border: 1px solid #262a33; border-radius: 8px; overflow: hidden; }
  .pub-vtitle { padding: 9px 11px; font-size: 13px; line-height: 1.3; }
  .pub-buy { display: block; width: calc(100% - 18px); margin: 0 9px 10px; padding: 7px 0; border: 0; border-radius: 6px; background: #3a78ff; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; }
  .pub-buy:hover { background: #2f63d6; }
  .pub-buy:disabled { opacity: .6; cursor: default; }
  .pub-buy[disabled] { background: #2a2f3a; color: #8b909a; cursor: not-allowed; }
  .pub-free { padding: 0 11px 10px; font-size: 12px; color: #6fcf97; }
  .pub-lockbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin: 0 0 22px; padding: 14px 16px; border: 1px solid rgba(255,209,102,.35); background: rgba(255,209,102,.08); border-radius: 10px; }
  .pub-lockbar-text { flex: 1 1 240px; font-size: 14px; color: #e6e8ec; }
  .pub-unlock { flex: 0 0 auto; border: 0; border-radius: 6px; background: #ffd166; color: #1a1d24; font-size: 14px; font-weight: 700; padding: 9px 18px; cursor: pointer; }
  .pub-unlock:hover { background: #ffc233; }
  .pub-unlock:disabled { opacity: .6; cursor: default; }
  .pub-state { padding: 40px 0; color: #9aa0a6; }
  .pub-error { color: #ff6b6b; }
  .pub-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.7); display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; z-index: 9999; overflow: auto; }
  .pub-modal { position: relative; width: 100%; max-width: 520px; background: #15181e; border: 1px solid #2a2f3a; border-radius: 12px; padding: 22px; }
  .pub-modal h3 { margin: 0 0 14px; color: #f5f5f7; font-size: 18px; padding-right: 28px; }
  .pub-modal-x { position: absolute; top: 12px; right: 14px; background: transparent; border: 0; color: #9aa0a6; font-size: 26px; line-height: 1; cursor: pointer; }
  .pub-modal-x:hover { color: #fff; }
`;

function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function thumb(image) {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.medium || image.small || image.large || "";
}

function imgOrPlaceholder(src) {
  return src
    ? '<img src="' + src + '" alt="" onerror="this.style.display=\'none\'"/>'
    : '<div class="pub-ph">No image</div>';
}

async function fetchJson(url, apiKey) {
  // buildHeader sends the subscriber's Bearer token when logged in (so paywall
  // checks reflect their purchases) and falls back to the tenant key otherwise.
  const res = await fetch(url, { headers: buildHeader(apiKey) });
  return res.json();
}

async function renderList(mount, baseUrl, apiKey) {
  mount.innerHTML = '<div class="pub-wrap"><h1>Publishers</h1>' +
    '<p class="pub-sub">Tap a publisher to view its content.</p>' +
    '<div id="pub-list" class="pub-state">Loading publishers…</div></div>';
  const host = mount.querySelector("#pub-list");
  try {
    const data = await fetchJson(baseUrl + "/clients/publishers/", apiKey);
    const list = Array.isArray(data) ? data : (data.results || []);
    if (!list.length) { host.className = "pub-state"; host.textContent = "No publishers found."; return; }
    host.className = "pub-grid";
    host.innerHTML = list.map(function (p) {
      const id = p.sub_tenant_uuid || p.uuid;
      // Raw path — the global anchor router (tempRouting) appends ".html" on click.
      const href = "/publisher-content?sub_tenant_id=" + encodeURIComponent(id) +
        "&title=" + encodeURIComponent(p.title || "");
      return '<a class="pub-card" href="' + href + '">' +
        '<div class="pub-thumb">' + imgOrPlaceholder(thumb(p.image)) +
          (p.requires_purchase ? '<span class="pub-badge">Paid</span>' : "") + "</div>" +
        '<div class="pub-meta"><div class="pub-title">' + (p.title || "Untitled") + "</div></div>" +
        "</a>";
    }).join("");
  } catch (e) {
    host.className = "pub-state pub-error";
    host.textContent = "Failed to load publishers: " + ((e && e.message) || e);
  }
}

// Look up this publisher's own paywall state (is it a paid publisher, and is it
// still locked for the current viewer?) from the publishers list.
async function fetchPublisher(baseUrl, apiKey, subTenantId) {
  try {
    const data = await fetchJson(baseUrl + "/clients/publishers/", apiKey);
    const list = Array.isArray(data) ? data : (data.results || []);
    return list.find(function (p) { return (p.sub_tenant_uuid || p.uuid) === subTenantId; }) || null;
  } catch (e) {
    return null;
  }
}

async function renderContent(mount, baseUrl, apiKey) {
  const params = new URLSearchParams(window.location.search);
  const subTenantId = params.get("sub_tenant_id");
  const title = params.get("title") || "Publisher Content";
  PUB_CTX = { baseUrl: baseUrl, apiKey: apiKey, subTenantId: subTenantId, publisherLocked: false };
  mount.innerHTML = '<div class="pub-wrap">' +
    '<a class="pub-back" href="/publishers">&larr; All publishers</a>' +
    "<h1>" + title + "</h1>" +
    '<div id="pub-banner"></div>' +
    '<div id="pub-content" class="pub-state">Loading content…</div></div>';
  const host = mount.querySelector("#pub-content");
  const bannerHost = mount.querySelector("#pub-banner");
  if (!subTenantId) { host.className = "pub-state pub-error"; host.textContent = "Missing sub_tenant_id."; return; }

  // Publisher-level paywall first: if the publisher itself is paid and not yet
  // unlocked, the parent must be paid before any of the publisher's content can
  // be purchased. Show an unlock gate and block content buys until it clears.
  const publisher = await fetchPublisher(baseUrl, apiKey, subTenantId);
  const publisherLocked = !!(publisher && publisher.requires_purchase && publisher.is_require_access);
  PUB_CTX.publisherLocked = publisherLocked;
  PUB_CTX.publisher = publisher;
  if (publisherLocked) {
    bannerHost.innerHTML =
      '<div class="pub-lockbar">' +
      '<div class="pub-lockbar-text"><strong>This publisher is locked.</strong> ' +
      'Unlock it to purchase and watch its content. ' +
      'This payment goes to the channel owner.</div>' +
      '<button id="pub-unlock-btn" class="pub-unlock">Unlock publisher</button>' +
      '</div>';
    const ub = bannerHost.querySelector("#pub-unlock-btn");
    if (ub) ub.addEventListener("click", function () { openUnlock(publisher, ub); });
  }

  try {
    const url = baseUrl + "/clients/vod-category/?type=on_demand&has_video=true&page=1&page_size=50&sub_tenant_id=" +
      encodeURIComponent(subTenantId);
    const data = await fetchJson(url, apiKey);
    const categories = (data && data.results) || [];
    if (!categories.length) { host.className = "pub-state"; host.textContent = "This publisher has no content yet."; return; }
    host.className = "";
    host.innerHTML = categories.map(function (cat) {
      const videos = cat.videos || [];
      const vhtml = videos.length
        ? videos.map(function (v) {
            const vid = v.id || v.uuid;
            VIDEO_INDEX[vid] = v;
            const locked = !!v.is_require_access;
            let action;
            if (publisherLocked) {
              action = '<button class="pub-buy" disabled title="Unlock the publisher first">Unlock publisher first</button>';
            } else if (locked) {
              action = '<button class="pub-buy" data-video-uuid="' + vid + '">Buy to watch</button>';
            } else {
              action = '<div class="pub-free">Accessible</div>';
            }
            return '<div class="pub-video">' +
              '<div class="pub-thumb">' +
              imgOrPlaceholder(thumb(v.thumbnail || v.h_thumbnail)) +
              (locked ? '<span class="pub-lock">Locked</span>' : "") +
              '</div><div class="pub-vtitle">' + (v.title || "Untitled") + "</div>" +
              action +
              "</div>";
          }).join("")
        : '<p class="pub-sub">No videos in this category.</p>';
      return '<div class="pub-category"><h2>' + (cat.title || "Untitled") + "</h2>" +
        '<div class="pub-row">' + vhtml + "</div></div>";
    }).join("");

    // Delegated buy handler for the (enabled) content buy buttons.
    host.addEventListener("click", function (e) {
      const btn = e.target.closest && e.target.closest(".pub-buy");
      if (!btn || btn.disabled) return;
      const v = VIDEO_INDEX[btn.getAttribute("data-video-uuid")];
      if (v) {
        openPurchase(v, btn).catch(function (err) {
          alert("Could not open the payment form: " + ((err && err.message) || err));
        });
      }
    });
  } catch (e) {
    host.className = "pub-state pub-error";
    host.textContent = "Failed to load content: " + ((e && e.message) || e);
  }
}

// Fetch the full video (scoped to the publisher) so we have its paywall
// packages and title for the payment form. The vod-category video items expose
// their id under the "id" field, not "uuid".
async function fetchVideoDetail(video) {
  if (video.packages && video.packages.length) return video;
  const id = video.id || video.uuid;
  try {
    const url = PUB_CTX.baseUrl + "/media/single/2/" + encodeURIComponent(id) +
      "?sub_tenant_id=" + encodeURIComponent(PUB_CTX.subTenantId);
    const detail = await fetchJson(url, PUB_CTX.apiKey);
    return detail && (detail.id || detail.uuid) ? detail : video;
  } catch (e) {
    return video;
  }
}

function closePurchaseModal() {
  const ex = document.getElementById("pub-modal");
  if (ex) ex.remove();
}

// Build a modal whose inner element is the container the PPV card renders into.
// It stays hidden until the card (or a selection step's result) actually fills
// it, so setupPPV's own choice/multi-PPV modals show on top without an empty
// backdrop behind them. Returns the container element to pass to setupPPV.
function makePaymentContainer() {
  closePurchaseModal();
  const overlay = document.createElement("div");
  overlay.id = "pub-modal";
  overlay.className = "pub-modal-overlay";
  overlay.style.display = "none";
  overlay.innerHTML =
    '<div class="pub-modal">' +
    '<button class="pub-modal-x" aria-label="Close">&times;</button>' +
    '<div id="pub-pay-container"></div>' +
    "</div>";
  overlay.addEventListener("click", function (e) {
    if (e.target === overlay || (e.target.classList && e.target.classList.contains("pub-modal-x"))) {
      closePurchaseModal();
    }
  });
  document.body.appendChild(overlay);
  const container = overlay.querySelector("#pub-pay-container");
  const obs = new MutationObserver(function () {
    if (container.children.length) overlay.style.display = "flex";
  });
  obs.observe(container, { childList: true });
  return container;
}

// Pay the parent to unlock a paid publisher. The publisher's packages are
// presented as VOD-type, so we drive setupPPV in single-content mode (data =
// publisher) — it renders the same plan-vs-PPV / multi-PPV selection used site
// wide by filtering on content_type only (no related_ids dependency). Routed to
// the parent's gateway (subTenantId = null).
async function openUnlock(publisher, btn) {
  if (!(publisher.packages && publisher.packages.length)) {
    alert("This publisher has no unlock package configured.");
    return;
  }
  if (btn) { btn.disabled = true; }
  const container = makePaymentContainer();
  await setupPPV(
    PUB_CTX.baseUrl,
    PUB_CTX.apiKey,
    publisher,
    container,
    ContentType.VOD,
    {},
    true,
    false,
    null // parent's gateway
  );
  if (btn) { btn.disabled = false; }
}

// Buy the publisher's content. The layered paywall returns the active gate: if
// the publisher is still locked it returns the publisher's own (TENANT) package
// — in that case route to the publisher unlock (parent gateway). Otherwise the
// video's own packages are charged to the sub tenant's gateway.
async function openPurchase(video, btn) {
  if (btn) { btn.disabled = true; btn.textContent = "Loading…"; }
  const detail = await fetchVideoDetail(video);
  if (btn) { btn.disabled = false; btn.textContent = "Buy to watch"; }
  const packages = (detail && detail.packages) || [];

  // Publisher gate still active -> must unlock the publisher first (parent).
  const hasPublisherGate = packages.some(function (p) { return Number(p.content_type) === ContentType.TENANT; });
  if (PUB_CTX.publisherLocked || hasPublisherGate) {
    if (PUB_CTX.publisher) {
      await openUnlock(PUB_CTX.publisher);
    } else {
      alert("This publisher must be unlocked before you can purchase its content.");
    }
    return;
  }

  if (!packages.length) {
    alert("No purchase options found for this content (it may be free or already accessible).");
    return;
  }

  // The video's paywall packages may be VIDEO-type (set per-video) or VOD-type
  // (set on its category). Pick the content_type that matches the packages so
  // setupPPV keeps them (mirrors the video player's setupPPV call).
  let contentType = ContentType.VIDEO;
  if (packages.filter(function (p) { return p.content_type == ContentType.VOD; }).length) {
    contentType = ContentType.VOD;
  } else if (packages.filter(function (p) { return p.content_type == ContentType.VIDEO; }).length) {
    contentType = ContentType.VIDEO;
  }

  const container = makePaymentContainer();
  await setupPPV(
    PUB_CTX.baseUrl,
    PUB_CTX.apiKey,
    detail,
    container,
    contentType,
    {},
    true,
    false,
    PUB_CTX.subTenantId // sub tenant's gateway
  );
}

// Deep-link payment page for the apps: open the payment form directly without
// the browse UI. URL params:
//   target        "publisher" (category unlock -> parent gateway) | "content"
//                 (the publisher's own content -> sub tenant gateway)
//   sub_tenant_id the publisher's sub tenant uuid (required)
//   content_id    the content uuid (required when target=content)
// The existing app-redirect params are honoured for the variant shown:
//   paymentType=ppv|subscription, ppvId=<package uuid>, planId=<plan uuid>.
// With none of those, the standard plan-vs-PPV / multi-PPV selection is shown.
async function renderDirectPayment(mount, baseUrl, apiKey) {
  const params = new URLSearchParams(window.location.search);
  const target = (params.get("target") || "publisher").toLowerCase();
  const subTenantId = params.get("sub_tenant_id");
  const contentId = params.get("content_id");
  PUB_CTX = { baseUrl: baseUrl, apiKey: apiKey, subTenantId: subTenantId, publisherLocked: false, publisher: null };

  mount.innerHTML = '<div class="pub-wrap"><div id="pub-pd" class="pub-state">Loading payment options…</div></div>';
  const host = mount.querySelector("#pub-pd");
  if (!subTenantId) {
    host.className = "pub-state pub-error";
    host.textContent = "Missing sub_tenant_id.";
    return;
  }

  try {
    // Resolve the publisher up front so a locked-publisher gate on content can
    // route to the publisher unlock.
    PUB_CTX.publisher = await fetchPublisher(baseUrl, apiKey, subTenantId);

    if (target === "content") {
      if (!contentId) {
        host.className = "pub-state pub-error";
        host.textContent = "Missing content_id.";
        return;
      }
      await openPurchase({ id: contentId });
    } else {
      if (!PUB_CTX.publisher) {
        host.className = "pub-state pub-error";
        host.textContent = "Publisher not found.";
        return;
      }
      await openUnlock(PUB_CTX.publisher);
    }
    // The payment card/selection renders in its own modal; clear the loader.
    host.textContent = "";
  } catch (e) {
    host.className = "pub-state pub-error";
    host.textContent = "Failed to load payment options: " + ((e && e.message) || e);
  }
}

export async function getPublishers(settings, baseUrl) {
  const path = window.location.pathname;
  const apiKey = settings && settings.apiKey;
  const isPayment = path.endsWith("publisher-payment.html") || document.getElementById("app-publisher-payment");
  const isContent = path.endsWith("publisher-content.html") || document.getElementById("app-publisher-content");
  const isList = path.endsWith("publishers.html") || document.getElementById("app-publishers");
  if (!isPayment && !isContent && !isList) return;

  injectStyle();
  const mount =
    document.getElementById("app-publisher-payment") ||
    document.getElementById("app-publisher-content") ||
    document.getElementById("app-publishers") ||
    document.getElementById("app-container");
  if (!mount) return;

  if (isPayment) {
    await renderDirectPayment(mount, baseUrl, apiKey);
  } else if (isContent) {
    await renderContent(mount, baseUrl, apiKey);
  } else {
    await renderList(mount, baseUrl, apiKey);
  }
}
